// ADR 6.9.26c §1 — one gate, both SDKs.
//
// Neither SDK is constructed before the gate is read, and neither is *imported*
// before it either: the two `import()`s (now in `lifecycle.ts`, 30.9.26i §2.2)
// are dynamic, so a denied visitor does not merely skip `init` — the chunks are
// never fetched. That is also what keeps them out of the entry chunk §4 puts a
// ceiling on; a static `import 'posthog-js'` in a module the entry reaches
// would eat most of the headroom that budget is claiming.
//
// Nothing here is awaited before the first render (`main.tsx`). 5.9.26i awaits
// `initI18n` on purpose — the alternative is a frame of raw catalogue keys, and
// on RTL a frame that then flips — and putting a cross-origin round-trip on that
// same critical path would trade a real flash for a real stall, with an API
// outage becoming the blank page §2 exists to catch.
//
// ADR 30.9.26i Open question 1, answered against `posthog-js` 1.427.2
// (`lib/src/posthog-core.js`, `_loaded`): the initial `$pageview` is sent
// *after* `config.loaded` runs, on a `setTimeout(…, 1)`, and only if the
// instance is opted in at that moment. So §2.5's clear from `loaded` lands
// first, and the first load after a re-grant keeps its pageview — the
// `opt_out_capturing()` teardown stays, and no `set_config` variant is needed.

import { authedJson } from '../api/http'
import type { PrivacyRow } from '../api/settings'
import { detachTelemetry, reportNonFatal } from './capture'
import {
  DEFAULT_CONSENT,
  deniesEverything,
  isTelemetryConsent,
  keepAnswered,
  mergeConsent,
  readLocalConsent,
  writeLocalConsent,
  type TelemetryConsent,
} from './consent'
import {
  claimBoot,
  CONSENT_FIELD as FIELD,
  CONSENT_SDKS as SDKS,
  construct,
  grantEpoch,
  reportInitOutcomes,
  resetLifecycleForTests,
  withdraw,
  type SessionProperties,
} from './lifecycle'

/**
 * Construct neither, one, or both SDKs, according to `consent`.
 *
 * The consent is read at *construction* rather than applied by a later
 * `optOut()` call — ADR 30.7.26a's finding, one client on: an SDK constructed
 * and then opted out has already emitted its own automatic lifecycle events
 * (`$pageview`, `$autocapture`), which is the leak the toggle exists to prevent.
 *
 * Never awaited by the caller, and **never rejects**. Both matter, and the
 * second is not decoration: `main.tsx` fires this with `void`, so a rejection
 * here would be an unhandled one — reported to Sentry as a fatal by the very
 * SDK that failed to load, or, when it is Sentry's own import that failed,
 * logged by the browser as an uncaught error at start-up. A CSP that forgot
 * one ingest origin, or a visitor behind a filtering proxy, is exactly the
 * case; neither should look like a crash. So a failed SDK is a `console.warn`
 * and an app that runs unmeasured, which is the same outcome as consent
 * having been denied.
 *
 * ADR 8.9.26b §3 adds the report a *granting* session leaves behind when that
 * happens, so a session that consented and was measured anyway is legible from
 * outside the browser it happened in. Once per page (`claimBoot`); the
 * `superProperties` are kept for a later mid-session construction (30.9.26i
 * §2.2).
 */
export async function initTelemetry(
  consent: TelemetryConsent,
  superProperties: SessionProperties = {},
): Promise<void> {
  if (!claimBoot(superProperties)) return
  if (deniesEverything(consent)) return
  const sdks = SDKS.filter((sdk) => !consent[FIELD[sdk]])
  reportInitOutcomes(sdks, await Promise.allSettled(sdks.map((sdk) => construct(sdk))))
}

/** What the reconcile settled on, and the row it read (`null` when it read none). */
export interface AccountRowReconcile {
  consent: TelemetryConsent
  row: PrivacyRow | null
}

/** The row as the panel may use it: the pair, and the ads flag only when it is a boolean. */
function rowOf(server: TelemetryConsent & { ads_personalisation_opt_out?: unknown }): PrivacyRow {
  const ads = server.ads_personalisation_opt_out
  return {
    analytics_opt_out: server.analytics_opt_out,
    crash_reports_opt_out: server.crash_reports_opt_out,
    ...(typeof ads === 'boolean' ? { ads_personalisation_opt_out: ads } : {}),
  }
}

/**
 * Pull the account row and merge it into the local record, most-restrictive-wins,
 * tearing down whichever SDK the merge withdrew — and only that one (30.9.26i
 * §2.2: the old single detach silenced crash reports when only analytics was
 * withdrawn).
 *
 * On a 401 or a network error the local record stands unchanged: the account
 * row is a reconcile input, not the source of truth (30.7.26a).
 *
 * **A stale row cannot undo a fresh grant** (§2.3). The local record is
 * re-read *after* the `GET`, and a field whose grant epoch moved while the
 * request was in flight keeps its local value — the learner's own `PUT` has
 * just written the row. A withdrawal needs no epoch: merging anything into the
 * re-read record can only tighten it.
 *
 * Safe to re-run on any TanStack refetch: against the same row a second run
 * changes nothing.
 */
export async function reconcileAccountRow(): Promise<AccountRowReconcile> {
  const before = { analytics: grantEpoch('analytics'), crash_reports: grantEpoch('crash_reports') }
  let server: unknown
  try {
    server = await authedJson<unknown>('/settings/privacy')
  } catch {
    return { consent: readLocalConsent(), row: null }
  }
  const local = readLocalConsent()
  // A 200 whose body is not the pair is the failure branch too. `authedJson`
  // casts rather than validates, and `local || server.x` over an absent field
  // would write `undefined` into the consent record.
  if (!isTelemetryConsent(server)) return { consent: local, row: null }

  const merged = mergeConsent(local, server)
  for (const sdk of SDKS) {
    if (grantEpoch(sdk) !== before[sdk]) merged[FIELD[sdk]] = local[FIELD[sdk]]
  }
  // ADR 30.9.26l §1 — preserve a person's answer, never invent one.
  writeLocalConsent(keepAnswered(merged))
  // The merge can only ever remove consent, so the only action it can require
  // is a teardown — never a construction. Guarded because this runs under a
  // `void` in `main.tsx`: a throw out of a torn SDK would become an unhandled
  // rejection at start-up.
  try {
    for (const sdk of SDKS) {
      if (merged[FIELD[sdk]] && !local[FIELD[sdk]]) withdraw(sdk)
    }
  } catch (error) {
    console.warn('telemetry teardown failed', error)
  }
  return { consent: merged, row: rowOf(server) }
}

/** The start-up caller (`main.tsx`), after the first render and never before it. */
export async function reconcileConsent(): Promise<TelemetryConsent> {
  return (await reconcileAccountRow()).consent
}

/** Test seam: forget that a page ever initialised telemetry. */
export function resetTelemetryForTests(): void {
  resetLifecycleForTests()
  detachTelemetry()
}

// ADR 6.9.26n §4 — the crash rail's one new door, exported *here* rather than
// from a module of its own: a file written against `@sentry/react` directly
// could neither reach the instance this one constructs nor be observed by a
// test. The same move 6.9.26m §1b makes with `identity.ts`.
export { DEFAULT_CONSENT, readLocalConsent, reportNonFatal, type SessionProperties, type TelemetryConsent }
